import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, Settings, LogOut, ShieldCheck, Menu } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { APP_NAME } from "../../utils/constants";
import { color, radius, shadow } from "../../styles/theme";
import useViewport from "../../hooks/useViewport";
import LanguageSwitcher from "../common/LanguageSwitcher";
import NotificationBell from "./NotificationBell";

const Navbar = ({ onToggleSidebar }) => {
  const { t } = useTranslation("layout");
  const { user, logout, isSubAccount, ownerBusinessName } = useAuth();
  const navigate = useNavigate();
  const { isMobile } = useViewport();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const displayName = user?.Name || user?.UserName || user?.Email || t("navbar.defaultDisplayName");
  const initials = displayName.charAt(0).toUpperCase();

  return (
    <header style={{ ...s.navbar, padding: isMobile ? "0 10px" : "0 24px" }}>
      <div style={s.brandWrap}>
        {onToggleSidebar && (
          <button type="button" style={s.hamburgerBtn} onClick={onToggleSidebar} aria-label={t("navbar.toggleMenu")}>
            <Menu size={20} strokeWidth={2.25} />
          </button>
        )}
        <img src="/PS_favicon.png" alt="PS" style={s.brandIcon} />
        {!isMobile && <span style={s.brand}>{APP_NAME}</span>}
        {isSubAccount && !isMobile && (
          <span style={s.subAccountBadge} title={t("navbar.actingAsSubAccountTitle", { business: ownerBusinessName })}>
            <ShieldCheck size={12} strokeWidth={2.25} />
            {t("navbar.actingAsSubAccount", { business: ownerBusinessName })}
          </span>
        )}
      </div>

      <div style={{ ...s.right, gap: isMobile ? 6 : 10 }}>
        <NotificationBell />
        <LanguageSwitcher />
        <div ref={menuRef} style={{ position: "relative" }}>
          <button style={s.avatarBtn} onClick={() => setMenuOpen((v) => !v)} aria-label={t("navbar.userMenu")}>
            <span style={s.avatar}>{initials}</span>
            {!isMobile && <span style={s.displayName}>{displayName}</span>}
            <ChevronDown size={14} strokeWidth={2.25} style={{ transform: menuOpen ? "rotate(180deg)" : "none", transition: "transform 150ms" }} />
          </button>

          {menuOpen && (
            <div className="modal-pop" style={s.dropdown}>
              <div style={s.dropdownHeader}>
                <span style={s.dropdownName}>{displayName}</span>
                {user?.Email && <span style={s.dropdownEmail}>{user.Email}</span>}
              </div>
              <div style={s.divider} />
              <button className="row-link" style={s.dropdownItem} onClick={() => { setMenuOpen(false); navigate("/settings"); }}>
                <Settings size={15} strokeWidth={2} /> {t("navbar.accountSettings")}
              </button>
              <div style={s.divider} />
              <button className="row-link" style={{ ...s.dropdownItem, color: color.danger500 }} onClick={logout}>
                <LogOut size={15} strokeWidth={2} /> {t("navbar.logout")}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

const s = {
  navbar: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    height: 58, padding: "0 24px", background: color.gray900,
    borderBottom: "1px solid rgba(255,255,255,0.06)", position: "sticky", top: 0, zIndex: 100,
  },
  brandWrap: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
  hamburgerBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 34, height: 34, flexShrink: 0, marginRight: 2,
    background: "transparent", border: "none", borderRadius: radius.sm,
    color: "#d1d5db", cursor: "pointer",
  },
  subAccountBadge: {
    display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700,
    color: color.primary100, background: "rgba(108,99,255,0.16)",
    padding: "4px 9px", borderRadius: radius.pill, marginLeft: 4, maxWidth: 220,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  brandIcon: { width: 28, height: 28, borderRadius: 7 },
  brand: { fontWeight: 700, fontSize: 16.5, color: "#fff", letterSpacing: -0.1 },
  right: { display: "flex", alignItems: "center", gap: 10 },
  avatarBtn: {
    display: "flex", alignItems: "center", gap: 9, background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.09)", borderRadius: radius.sm, padding: "6px 10px 6px 6px",
    cursor: "pointer", color: "#fff", transition: "background 150ms",
  },
  avatar: {
    width: 28, height: 28, borderRadius: "50%", background: color.primary500, color: "#fff",
    display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 12.5, flexShrink: 0,
  },
  displayName: {
    fontSize: 13, fontWeight: 500, color: "#d1d5db", maxWidth: 140,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  dropdown: {
    position: "absolute", top: "calc(100% + 8px)", right: 0, width: 220,
    background: color.surface, borderRadius: radius.md, boxShadow: shadow.lg,
    border: `1px solid ${color.border}`, overflow: "hidden", zIndex: 200,
  },
  dropdownHeader: { padding: "14px 16px 10px" },
  dropdownName: { display: "block", fontWeight: 700, fontSize: 13.5, color: color.textPrimary },
  dropdownEmail: { display: "block", fontSize: 11.5, color: color.textMuted, marginTop: 2 },
  divider: { height: 1, background: color.gray100 },
  dropdownItem: {
    width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "11px 16px",
    background: "transparent", border: "none", fontSize: 13, color: color.gray700,
    fontWeight: 500, cursor: "pointer", textAlign: "left",
  },
};

export default Navbar;
